import styled from "@emotion/styled";
import { IconLetter } from '@kompare/kompare-ui-library';
import { useEffect, useRef, useState } from 'react';
import { i18n, mediaQuery, showMessage, useGeneralSoContext } from 'src/common';
import { useFinishPurchase } from "src/common/apiGraphql/mutations/useFinishPurchase.mutate";
import { useGeneratePolicy } from "src/common/apiGraphql/mutations/useGeneratePolicy.mutate";
import { useGetOffer } from 'src/common/apiGraphql/queries/useGetOffer.query';
import { WsPayResponseInput } from "src/common/apiGraphql/types/types";
import { Flex, StatusMessage, Text } from 'src/components';
import { colors, footer, header } from 'src/theme';

const FlexStyle = styled(Flex)({
  minHeight: `calc(100vh - ${footer.mobileHeight + header.mobileHeight + 80}px)`,

  [mediaQuery('mobile')]: {
    minHeight: `calc(100vh - ${footer.height + header.mobileHeight + 80}px)`,
  },

  [mediaQuery('laptopL')]: {
    minHeight: `calc(100vh - ${footer.height + header.height + 80}px)`,
  },
});

const OtherProductsStyle = styled.div({
  width: '100%',
  paddingBottom: 40,
});

const OtherProductsTitleStyle = styled(Text)({
  textAlign: 'center',
  textTransform: 'uppercase',
  letterSpacing: 2,
  marginBottom: 24,
});

const ProductCardsStyle = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 16,

  [mediaQuery('mobile')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },

  [mediaQuery('laptop')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
});

const ProductCardStyle = styled.a({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 16,
  padding: 24,
  background: colors.white,
  borderRadius: 4,
  textAlign: 'center',
  textDecoration: 'none',
});

const ProductImageStyle = styled.img({
  height: 80,
  maxWidth: '100%',
  objectFit: 'contain',
});

const otherProducts = [
  {
    key: 'auto',
    image: 'https://kompare.hr/wp-content/uploads/2024/10/Auto-KRediti.webp',
    link: 'https://kompare.hr/osiguranje/auto-osiguranje/',
  },
  {
    key: 'dopunsko',
    image: 'https://kompare.hr/wp-content/uploads/2023/10/Kompare-Hzzo.webp',
    link: 'https://kompare.hr/osiguranje/dopunsko-zdravstveno-osiguranje/',
  },
  {
    key: 'telco',
    image: 'https://kompare.hr/wp-content/uploads/2024/10/telco-hero-kampanja.webp',
    link: 'https://kompare.hr/telekomunikacije/telefon-internet-tv/',
  },
  {
    key: 'krediti',
    image: 'https://kompare.hr/wp-content/uploads/2024/01/Novcanik-kartice.png',
    link: 'https://kompare.hr/krediti/',
  },
];

type UrlParams = {
  [key: string]: string;
};

// Definišite mapu za mapiranje starih ključeva na nove ključeve
const keyMapping: { [key: string]: string } = {
  CustomerFirstname: 'wsPayCustomerFirstName',
  CustomerSurname: 'wsPayCustomerLastName',
  ShoppingCartID: 'wsPayShoppingCartId',
  DateTime: 'wsPayDateTime',
  Amount: 'wsPayAmount',
  STAN: 'wsPaySTAN',
  Partner: 'wsPayPartner',
  WsPayOrderId: 'wsPayOrderId',
  PaymentType: 'wsPayPaymentType',
  CreditCardNumber: 'wsPayCreditCardNumber',
  PaymentPlan: 'wsPayPaymentPlan',
  Success: 'wsPaySuccess',
  ApprovalCode: 'wsPayApprovalCode',
  Signature: 'wsPayConfirmationSignature',
};

const CheckoutSuccessPage = () => {
  const [params, setParams] = useState<UrlParams | null>(null);
  const purchaseCompletedRef = useRef(false);
  const trackingCompletedRef = useRef(false);

  const { offerId, soId } = useGeneralSoContext();
  const { offer, refetch: refetchOffer } = useGetOffer();
  const [finishPurchase] = useFinishPurchase();
  const [generatePolicy] = useGeneratePolicy();


  useEffect(() => {
    if (!soId || !offerId) {
      return;
    }
    if (offer) {
      refetchOffer();
    }
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const paramsObject: UrlParams = Array.from(queryParams.entries()).reduce((acc, [key, value]) => {
      if (keyMapping[key]) {
        const newKey = keyMapping[key];
        acc[newKey] = value;
      }
      return acc;
    }, {} as UrlParams);
    setParams(paramsObject);
  }, []);

  useEffect(() => {
    if (params && !purchaseCompletedRef.current) {
      purchaseCompletedRef.current = true;

      const paramsObject: WsPayResponseInput = {
        wsPayAmount: params.wsPayAmount,
        wsPayApprovalCode: params.wsPayApprovalCode,
        wsPayConfirmationSignature: params.wsPayConfirmationSignature,
        wsPayCreditCardNumber: params.wsPayCreditCardNumber,
        wsPayCustomerFirstName: params.wsPayCustomerFirstName,
        wsPayCustomerLastName: params.wsPayCustomerLastName,
        wsPayDateTime: params.wsPayDateTime,
        wsPayOrderId: params.wsPayOrderId,
        wsPayPartner: params.wsPayPartner,
        wsPayPaymentPlan: params.wsPayPaymentPlan,
        wsPayPaymentType: params.wsPayPaymentType,
        wsPaySTAN: params.wsPaySTAN,
        wsPayShoppingCartId: params.wsPayShoppingCartId,
        wsPaySuccess: params.wsPaySuccess,
      };
      finishPurchase(paramsObject).then(() => {
        generatePolicy();
      }).catch(() => {
        showMessage({
          type: 'error',
          message: i18n.t('common.messages.policyErrorMessage'),
          duration: 5000,
          fixed: true
        });
      }).finally(() => {
        window.sessionStorage.clear();
      });
    }
  }, [params, finishPurchase]);

  // Track purchase event in GTM on page load when params and offer are available
  useEffect(() => {
    if (params && offer && !trackingCompletedRef.current) {
      // Mark as tracked to prevent duplicate events
      trackingCompletedRef.current = true;
      window.dataLayer = window.dataLayer || [];

      // Clear previous ecommerce data
      window.dataLayer.push({ ecommerce: null });

      // Push purchase event
      window.dataLayer.push({
        event: 'eec.purchase',
        product_category: 'Putno osiguranje',
        trackingData: {
          category: 'Putno osiguranje',
          transactionId: params.wsPayOrderId || offer.offerNumber || offer._id,
          revenue: offer.productTypeSpecific?.price?.totalPrice || 0,
          currencyCode: 'EUR'
        },
        ecommerce: {
          transaction_id: params.transactionId || offer.offerNumber || offer._id,
          affiliation: '',
          value: offer.productTypeSpecific?.price?.totalPrice || 0,
          tax: '',
          shipping: '',
          currency: 'EUR',
          coupon: '',
          items: [
            {
              item_id: offer.productId || '',
              item_name: offer.product?.name || '',
              item_category: offer.productType?.name || 'Putno osiguranje',
              item_brand: offer.product?.brand?.name || '',
              price: offer.productTypeSpecific?.price?.totalPrice || 0,
              quantity: 1
            }
          ]
        }
      });
    }
  }, [params, offer]);

  return (
    <>
      <FlexStyle justify='center' align='center'>
        <StatusMessage buttons={<></>} icon={<IconLetter color={colors.icon4} />} title={i18n.t('pages.checkoutCongratulationsPage.title')} subtitle={(offer?.product.productTypeSpecific?.offline || params?.offline === 'true') ? i18n.t('pages.checkoutCongratulationsPage.subtitleOffline') : i18n.t('pages.checkoutCongratulationsPage.subtitle')} />
      </FlexStyle>
      <OtherProductsStyle>
        <OtherProductsTitleStyle type='small' color={colors.icon4}>
          {i18n.t('pages.checkoutCongratulationsPage.otherProductsTitle')}
        </OtherProductsTitleStyle>
        <ProductCardsStyle>
          {otherProducts.map(({ key, image, link }) => (
            <ProductCardStyle key={key} href={link}>
              <ProductImageStyle src={image} alt={i18n.t(`pages.checkoutCongratulationsPage.otherProducts.${key}`)} loading='lazy' />
              <Text type='sMedium'>{i18n.t(`pages.checkoutCongratulationsPage.otherProducts.${key}`)}</Text>
            </ProductCardStyle>
          ))}
        </ProductCardsStyle>
      </OtherProductsStyle>
    </>
  );
}

export default CheckoutSuccessPage;